import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Handshake, Send, Inbox, X, Check, Loader2, Sparkles, Store, Coins, Tag } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { resolveImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

const SLOT_COSTS = { 3: 250, 4: 500, 5: 1000 };

function CardSnapshot({ snap }) {
  if (!snap) return <span className="text-[11px] italic text-[#64748b]">—</span>;
  const r = (snap.rarity || "common").toLowerCase();
  return (
    <span className="inline-flex items-center gap-1 font-heading text-[11px]">
      <span style={{ color: RARITY_COLOR[r] || "#e2e8f0" }}>{snap.name}</span>
      {snap.foil && <Sparkles size={10} className="text-[#fef08a]" />}
    </span>
  );
}

export default function Trade({ initialSelection, onSelectionConsumed }) {
  const { game, loadGame } = useGame();
  const { token, API } = useAuth();
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Create form state
  const [toTag, setToTag] = useState("");
  const [sourceCategory, setSourceCategory] = useState("objects");
  const [sourceCardId, setSourceCardId] = useState("");
  const [priceGold, setPriceGold] = useState(0);
  const [sending, setSending] = useState(false);
  const [targetPreview, setTargetPreview] = useState(null); // {tag, character:{name,class_type,level}, is_own}

  // Marketplace state
  const [marketListings, setMarketListings] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketListingCategory, setMarketListingCategory] = useState("objects");
  const [marketListingCardId, setMarketListingCardId] = useState("");
  const [marketListingPrice, setMarketListingPrice] = useState(0);
  const [marketListingSending, setMarketListingSending] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("direct");

  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");

  const getSellerName = (username) => {
    if (!username) return isIt ? "Viaggiatore" : "Traveler";
    if (username.toLowerCase() === "admin") return "erlupaccio";
    return username;
  };

  const fetchTrades = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [inRes, outRes] = await Promise.all([
        axios.get(`${API}/trade/incoming`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trade/outgoing`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setIncoming(inRes.data || []);
      setOutgoing(outRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load trades");
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const fetchMarketListings = useCallback(async () => {
    if (!token) return;
    setMarketLoading(true);
    try {
      const res = await axios.get(`${API}/market/listings`, { headers: { Authorization: `Bearer ${token}` } });
      setMarketListings(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load market listings");
    } finally {
      setMarketLoading(false);
    }
  }, [API, token]);

  const handleCreateMarketListing = async (e) => {
    e.preventDefault();
    if (!marketListingCardId || !game?.id) {
      toast.error(isIt ? "Seleziona una carta da vendere" : "Select a card to sell");
      return;
    }
    setMarketListingSending(true);
    try {
      await axios.post(
        `${API}/market/list`,
        {
          from_game_id: game.id,
          card_id: marketListingCardId,
          card_category: marketListingCategory,
          price_gold: Math.max(0, parseInt(marketListingPrice, 10) || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isIt ? "Carta messa in vetrina!" : "Card listed in showcase!");
      setMarketListingCardId("");
      setMarketListingPrice(0);
      await Promise.all([fetchMarketListings(), loadGame(game.id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to list card");
    } finally {
      setMarketListingSending(false);
    }
  };

  const handleBuyListing = async (listingId, price) => {
    if (!game?.id) return;
    const confirmBuy = window.confirm(
      isIt
        ? `Sei sicuro di voler acquistare questa carta per ${price} oro?`
        : `Are you sure you want to buy this card for ${price} gold?`
    );
    if (!confirmBuy) return;

    setBusyId(listingId);
    try {
      await axios.post(
        `${API}/market/listings/${listingId}/buy`,
        { buyer_game_id: game.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isIt ? "Acquisto completato!" : "Purchase completed!");
      await Promise.all([fetchMarketListings(), loadGame(game.id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelListing = async (listingId) => {
    setBusyId(listingId);
    try {
      await axios.post(
        `${API}/market/listings/${listingId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isIt ? "Vendita annullata, carta restituita" : "Sale cancelled, card returned");
      await Promise.all([fetchMarketListings(), loadGame(game.id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancellation failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlockSlot = async () => {
    if (!game?.id) return;
    const nextSlot = slots + 1;
    const cost = SLOT_COSTS[nextSlot];
    if (!cost) return;

    const confirmUnlock = window.confirm(
      isIt
        ? `Vuoi sbloccare lo Slot ${nextSlot} della vetrina per ${cost} oro?`
        : `Do you want to unlock Showcase Slot ${nextSlot} for ${cost} gold?`
    );
    if (!confirmUnlock) return;

    setUnlocking(true);
    try {
      await axios.post(
        `${API}/market/slots/unlock`,
        { game_id: game.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        isIt
          ? `Slot ${nextSlot} sbloccato con successo!`
          : `Slot ${nextSlot} successfully unlocked!`
      );
      await loadGame(game.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlock slot");
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    fetchTrades();
    fetchMarketListings();
  }, [fetchTrades, fetchMarketListings]);

  // Pre-fill category + card from an external selection (e.g. from Collection → Trade button)
  const formRef = useRef(null);
  useEffect(() => {
    if (!initialSelection || !initialSelection.card_id || !initialSelection.category) return;
    if (initialSelection.type === "market") {
      setActiveSubTab("market");
      setMarketListingCategory(initialSelection.category);
      setMarketListingCardId(initialSelection.card_id);
    } else {
      setActiveSubTab("direct");
      setSourceCategory(initialSelection.category);
      setSourceCardId(initialSelection.card_id);
    }
    // Scroll the form into view so the user immediately sees the prefilled selection
    setTimeout(() => {
      try { formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* ignore */ }
    }, 200);
    onSelectionConsumed?.();
  }, [initialSelection, onSelectionConsumed]);

  const inventory = game?.collection_cards || {};
  const currentCards = (inventory[sourceCategory] || []).filter(c => !c.is_basic);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!toTag || !sourceCardId || !game?.id) {
      toast.error(isIt ? "Inserisci tag e carta da offrire" : "Enter tag and card to offer");
      return;
    }
    const normalizedTag = toTag.trim().toUpperCase().startsWith("#") ? toTag.trim().toUpperCase() : `#${toTag.trim().toUpperCase()}`;
    setSending(true);
    try {
      await axios.post(
        `${API}/trade/create`,
        {
          to_tag: normalizedTag,
          from_game_id: game.id,
          from_card_id: sourceCardId,
          from_card_category: sourceCategory,
          price_gold: Math.max(0, parseInt(priceGold, 10) || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isIt ? "Offerta inviata!" : "Offer sent!");
      setToTag("");
      setSourceCardId("");
      setPriceGold(0);
      setTargetPreview(null);
      await Promise.all([fetchTrades(), loadGame(game.id)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  // Preview target game when tag is complete (#XXXX format, 5+ chars)
  const lookupTag = useCallback(async (raw) => {
    const t = (raw || "").trim().toUpperCase();
    const norm = t.startsWith("#") ? t : `#${t}`;
    if (norm.length < 5) { setTargetPreview(null); return; }
    try {
      const res = await axios.get(`${API}/game/by-tag/${encodeURIComponent(norm)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTargetPreview(res.data);
    } catch {
      setTargetPreview(null);
    }
  }, [API, token]);

  useEffect(() => {
    const id = setTimeout(() => { lookupTag(toTag); }, 350);
    return () => clearTimeout(id);
  }, [toTag, lookupTag]);

  const handleAccept = async (tradeId) => {
    setBusyId(tradeId);
    try {
      await axios.post(`${API}/trade/${tradeId}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isIt ? "Scambio accettato" : "Trade accepted");
      await loadGame(game.id);
      await fetchTrades();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
    } finally { setBusyId(null); }
  };

  const handleReject = async (tradeId) => {
    setBusyId(tradeId);
    try {
      await axios.post(`${API}/trade/${tradeId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isIt ? "Rifiutato" : "Rejected");
      await fetchTrades();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  const handleCancel = async (tradeId) => {
    setBusyId(tradeId);
    try {
      await axios.post(`${API}/trade/${tradeId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isIt ? "Annullato" : "Cancelled");
      await Promise.all([fetchTrades(), loadGame(game.id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setBusyId(null); }
  };

  if (!game) return null;

  const marketCards = (inventory[marketListingCategory] || []).filter(c => !c.is_basic);
  const myListings = marketListings.filter(l => l.seller_user_id === game?.user_id);
  const slots = game?.market_slots || 2;
  const isShowcaseFull = myListings.length >= slots;
  const nextSlotCost = SLOT_COSTS[slots + 1] || 0;

  const renderMarketCard = (lst, isOwn) => {
    const e = lst.card;
    if (!e) return null;
    const color = RARITY_COLOR[e.rarity] || "#94a3b8";
    const typeClass = e.type === "ally" ? "card-ally" : e.type === "ability" ? "card-ability" : "card-object";
    const rarityClass = e.rarity ? `rarity-${e.rarity.toLowerCase()}` : "rarity-common";
    const hasPlaque = e.type === "ally" || (e.type === "object" && ((e.attack || 0) > 0 || (e.defense || 0) > 0));

    return (
      <div key={lst.id} className="flex flex-col items-center bg-[#111115]/90 border border-[#2d3748]/60 hover:border-[#cfb53b]/40 rounded-lg p-2.5 text-center transition-all" data-testid={`market-item-${lst.id}`}>
        {/* Compact Card Render */}
        <div className="w-28 mb-2 flex justify-center">
          <div className={`mtg-card ${typeClass} ${rarityClass} variant-compact`}>
            <div className={`mtg-inner-frame ${e.foil ? "card-foil" : ""}`}>
              {/* Header bar: Title + Mana */}
              <div className="mtg-header-bar">
                <span className="mtg-card-title">{e.name}</span>
                <div className="mtg-header-right">
                  {e.level > 1 && (
                    <span className="mtg-level-badge">Lv{e.level}</span>
                  )}
                  {e.mana_cost > 0 && (
                    <div className="mtg-mana-gem">
                      <span>{e.mana_cost}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Artwork */}
              <div className="mtg-art-frame">
                {e.image_url ? (
                  <img
                    src={resolveImageUrl(e.image_url, BACKEND_URL)}
                    alt={e.name}
                    className="mtg-art-image"
                    loading="lazy"
                  />
                ) : (
                  <div className="mtg-no-art">
                    <Sparkles size={16} style={{ color }} />
                  </div>
                )}
              </div>

              {/* Stat plaque */}
              {hasPlaque && (
                <div className="mtg-stat-plaque">
                  {e.type === "ally" ? (
                    <>
                      <span>{e.attack}</span>/<span>{e.hp}</span>
                    </>
                  ) : (
                    <>
                      {e.attack > 0 && <span>+{e.attack}</span>}
                      {e.attack > 0 && e.defense > 0 && <span>/</span>}
                      {e.defense > 0 && <span>+{e.defense}</span>}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Seller Info & Price */}
        <div className="w-full text-center space-y-1 mb-2">
          {!isOwn && (
            <p className="text-[10px] text-[#64748b] truncate px-1" data-testid={`market-seller-${lst.id}`}>
              {isIt ? "Venditore" : "Seller"}: <span className="text-[#cfb53b] font-bold">{getSellerName(lst.seller_username)}</span>
            </p>
          )}
          <div className="inline-flex items-center gap-1 text-[11px] text-[#cfb53b] font-heading bg-[#cfb53b]/10 px-2 py-0.5 rounded">
            <Coins size={11} />
            <span>{lst.price_gold} {isIt ? "oro" : "gold"}</span>
          </div>
        </div>

        {/* Action Button */}
        {isOwn ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busyId === lst.id}
            onClick={() => handleCancelListing(lst.id)}
            className="w-full text-[#8b0000] hover:bg-[#8b0000]/10 border border-[#8b0000]/20 h-7 text-[10px] font-heading"
            data-testid={`market-cancel-${lst.id}`}
          >
            {busyId === lst.id ? <Loader2 size={10} className="animate-spin" /> : (isIt ? "Ritira" : "Cancel")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={busyId === lst.id}
            onClick={() => handleBuyListing(lst.id, lst.price_gold)}
            className="w-full bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30 h-7 text-[10px] font-heading"
            data-testid={`market-buy-${lst.id}`}
          >
            {busyId === lst.id ? <Loader2 size={10} className="animate-spin" /> : (isIt ? "Compra" : "Buy")}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="trade-view">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Handshake size={18} />
            {isIt ? "Scambio Carte" : "Card Trading"}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchTrades(); fetchMarketListings(); }}
            className="text-[#94a3b8] hover:text-[#cfb53b] h-7"
            data-testid="trade-refresh"
          >
            {loading || marketLoading ? <Loader2 size={14} className="animate-spin" /> : (isIt ? "Aggiorna" : "Refresh")}
          </Button>
        </div>

        <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
          <TabsList className="bg-[#1a1a1d] border border-[#cfb53b]/20 w-full grid grid-cols-2 mb-4">
            <TabsTrigger value="direct" className="font-heading text-sm" data-testid="trade-tab-direct">
              <Handshake size={13} className="mr-1.5" />
              {isIt ? "Scambi Diretti" : "Direct Trades"}
            </TabsTrigger>
            <TabsTrigger value="market" className="font-heading text-sm" data-testid="trade-tab-market" onClick={fetchMarketListings}>
              <Store size={13} className="mr-1.5" />
              {isIt ? "Vetrina Vendite" : "Sales Showcase"}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="direct" className="space-y-4">
            {/* Create trade form */}
            <form
              ref={formRef}
              onSubmit={handleCreate}
              className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-4 mb-4 space-y-3"
              data-testid="trade-form"
            >
              <h3 className="font-heading text-sm text-[#cfb53b] flex items-center gap-2">
                <Send size={14} /> {isIt ? "Invia un'offerta" : "Send an offer"}
              </h3>
              <div>
                <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                  {isIt ? "Tag destinatario" : "Recipient tag"}
                </label>
                <input
                  data-testid="trade-to-tag"
                  type="text"
                  required
                  value={toTag}
                  onChange={(e) => setToTag(e.target.value)}
                  placeholder="#A7K3"
                  maxLength={6}
                  className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#e2e8f0] focus:outline-none focus:border-[#cfb53b]/50 font-mono uppercase tracking-wider"
                />
                {targetPreview && (
                  <p className={`text-[10px] mt-1 ${targetPreview.is_own ? 'text-[#cfb53b]' : 'text-green-400'}`} data-testid="trade-target-preview">
                    {targetPreview.is_own && (isIt ? "(tua partita) " : "(your game) ")}
                    {targetPreview.character?.name} · {targetPreview.character?.class_type} · Lv {targetPreview.character?.level}
                  </p>
                )}
                {!targetPreview && toTag.trim().length >= 4 && (
                  <p className="text-[10px] mt-1 text-[#64748b] italic">
                    {isIt ? "Inserisci un tag valido (#XXXX) per vedere il destinatario" : "Enter a valid tag (#XXXX) to preview the recipient"}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                    {isIt ? "Categoria" : "Category"}
                  </label>
                  <select
                    data-testid="trade-category"
                    value={sourceCategory}
                    onChange={(e) => { setSourceCategory(e.target.value); setSourceCardId(""); }}
                    className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#e2e8f0] focus:outline-none focus:border-[#cfb53b]/50"
                  >
                    {React.createElement("option", { value: "objects" }, isIt ? "Oggetti" : "Objects")}
                    {React.createElement("option", { value: "allies" }, isIt ? "Alleati" : "Allies")}
                    {React.createElement("option", { value: "abilities" }, isIt ? "Abilità" : "Abilities")}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                    {isIt ? "Carta offerta" : "Offered card"}
                  </label>
                  <select
                    data-testid="trade-card"
                    value={sourceCardId}
                    onChange={(e) => setSourceCardId(e.target.value)}
                    required
                    className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#e2e8f0] focus:outline-none focus:border-[#cfb53b]/50"
                  >
                    {React.createElement("option", { value: "" }, isIt ? "Seleziona..." : "Select...")}
                    {currentCards.map(c =>
                      React.createElement(
                        "option",
                        { key: c.id, value: c.id },
                        `${c.name} ${c.foil ? "✨" : ""} [${(c.rarity || "common").toUpperCase()}]`
                      )
                    )}
                  </select>
                  {currentCards.length === 0 && (
                    <p className="text-[10px] text-[#64748b] italic mt-1">
                      {isIt ? "Nessuna carta scambiabile in questa categoria" : "No tradable cards in this category"}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                  {isIt ? "Prezzo in oro (opzionale)" : "Gold price (optional)"}
                </label>
                <input
                  data-testid="trade-price-gold"
                  type="number"
                  min={0}
                  max={999999}
                  value={priceGold}
                  onChange={(e) => setPriceGold(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#cfb53b] focus:outline-none focus:border-[#cfb53b]/50 font-heading"
                />
                <p className="text-[10px] text-[#64748b] mt-1">
                  {isIt
                    ? "Se > 0, il destinatario dovrà pagarti questa somma in oro per ricevere la carta."
                    : "If > 0, the recipient must pay this amount of gold to receive the card."}
                </p>
              </div>
              <Button
                type="submit"
                disabled={sending || !toTag || !sourceCardId}
                className="w-full bg-[#cfb53b]/10 text-[#cfb53b] hover:bg-[#cfb53b]/20 border border-[#cfb53b]/30 h-9"
                data-testid="trade-submit"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-2" />{isIt ? "Invia offerta" : "Send offer"}</>}
              </Button>
              <p className="text-[10px] text-[#64748b]">
                {isIt
                  ? "Suggerimento: puoi scambiare carte tra le tue diverse partite usando i rispettivi tag. Ogni partita ha un tag unico mostrato accanto al nome del personaggio."
                  : "Tip: you can trade cards between your own different games using their tags. Each game has a unique tag shown next to the character name."}
              </p>
            </form>

            {/* Incoming / Outgoing */}
            <Tabs defaultValue="incoming">
              <TabsList className="bg-[#1a1a1d] border border-[#cfb53b]/20 w-full grid grid-cols-2">
                <TabsTrigger value="incoming" data-testid="trade-tab-incoming" className="font-heading text-sm">
                  <Inbox size={13} className="mr-1.5" />
                  {isIt ? "In arrivo" : "Incoming"} ({incoming.length})
                </TabsTrigger>
                <TabsTrigger value="outgoing" data-testid="trade-tab-outgoing" className="font-heading text-sm">
                  <Send size={13} className="mr-1.5" />
                  {isIt ? "Inviate" : "Outgoing"} ({outgoing.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="incoming" className="mt-3 space-y-2">
                {incoming.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-[#2d3748] rounded-lg">
                    <Inbox size={32} className="mx-auto text-[#2d3748] mb-2" />
                    <p className="text-[#94a3b8] text-sm">{isIt ? "Nessuna offerta ricevuta" : "No incoming offers"}</p>
                  </div>
                ) : incoming.map(tr => (
                  <div key={tr.id} className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-3" data-testid={`trade-incoming-${tr.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="text-[11px] text-[#94a3b8]">
                        {isIt ? "Da" : "From"}: <span className="text-[#cfb53b] font-mono">{tr.from_game_tag || tr.from_user_email}</span>
                      </span>
                      <span className="text-[10px] text-[#64748b]">{new Date(tr.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-[#e2e8f0] mb-2 flex flex-wrap items-center gap-2">
                      <span>{isIt ? "Ti offre: " : "Offers: "}</span>
                      <CardSnapshot snap={tr.from_card_snapshot} />
                      {tr.price_gold > 0 && (
                        <span className="text-[11px] text-[#cfb53b] font-heading bg-[#cfb53b]/10 px-2 py-0.5 rounded">
                          💰 {tr.price_gold} {isIt ? "oro richiesti" : "gold required"}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(tr.id)}
                        disabled={busyId === tr.id}
                        className="flex-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30 h-7 text-[11px]"
                        data-testid={`trade-accept-${tr.id}`}
                      >
                        {busyId === tr.id ? <Loader2 size={12} className="animate-spin" /> : (
                          <>
                            <Check size={12} className="mr-1" />
                            {tr.price_gold > 0
                              ? (isIt ? `Paga ${tr.price_gold}` : `Pay ${tr.price_gold}`)
                              : (isIt ? "Accetta" : "Accept")}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReject(tr.id)}
                        disabled={busyId === tr.id}
                        variant="ghost"
                        className="flex-1 text-[#8b0000] hover:bg-[#8b0000]/10 border border-[#8b0000]/20 h-7 text-[11px]"
                        data-testid={`trade-reject-${tr.id}`}
                      >
                        <X size={12} className="mr-1" />{isIt ? "Rifiuta" : "Reject"}
                      </Button>
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="outgoing" className="mt-3 space-y-2">
                {outgoing.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-[#2d3748] rounded-lg">
                    <Send size={32} className="mx-auto text-[#2d3748] mb-2" />
                    <p className="text-[#94a3b8] text-sm">{isIt ? "Nessuna offerta inviata" : "No sent offers"}</p>
                  </div>
                ) : outgoing.map(tr => (
                  <div key={tr.id} className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-3" data-testid={`trade-outgoing-${tr.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="text-[11px] text-[#94a3b8]">
                        {isIt ? "A" : "To"}: <span className="text-[#cfb53b] font-mono">{tr.to_game_tag || tr.to_user_email}</span>
                      </span>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-heading uppercase ${
                        tr.status === "pending" ? "bg-yellow-500/15 text-yellow-400" :
                        tr.status === "accepted" ? "bg-green-500/15 text-green-400" :
                        tr.status === "rejected" ? "bg-red-500/15 text-red-400" :
                        "bg-[#2d3748] text-[#94a3b8]"
                      }`}>{tr.status}</span>
                    </div>
                    <div className="text-xs text-[#e2e8f0] mb-2 flex flex-wrap items-center gap-2">
                      <span>{isIt ? "Hai offerto: " : "You offered: "}</span>
                      <CardSnapshot snap={tr.from_card_snapshot} />
                      {tr.price_gold > 0 && (
                        <span className="text-[11px] text-[#cfb53b] font-heading bg-[#cfb53b]/10 px-2 py-0.5 rounded">
                          💰 {tr.price_gold} {isIt ? "oro" : "gold"}
                        </span>
                      )}
                    </div>
                    {tr.status === "pending" && (
                      <Button
                        size="sm"
                        onClick={() => handleCancel(tr.id)}
                        disabled={busyId === tr.id}
                        variant="ghost"
                        className="w-full text-[#8b0000] hover:bg-[#8b0000]/10 border border-[#8b0000]/20 h-7 text-[11px]"
                        data-testid={`trade-cancel-${tr.id}`}
                      >
                        {busyId === tr.id ? <Loader2 size={12} className="animate-spin" /> : <><X size={12} className="mr-1" />{isIt ? "Annulla" : "Cancel"}</>}
                      </Button>
                    )}
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="market" className="space-y-4">
            {/* Form per mettere in vendita */}
            <form
              onSubmit={handleCreateMarketListing}
              className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-4 space-y-3"
              data-testid="market-sell-form"
            >
              <h3 className="font-heading text-sm text-[#cfb53b] flex items-center gap-2">
                <Tag size={14} /> {isIt ? "Metti in vendita una carta" : "Sell a card"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                    {isIt ? "Categoria" : "Category"}
                  </label>
                  <select
                    data-testid="market-sell-category"
                    value={marketListingCategory}
                    onChange={(e) => { setMarketListingCategory(e.target.value); setMarketListingCardId(""); }}
                    className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#e2e8f0] focus:outline-none focus:border-[#cfb53b]/50"
                  >
                    {React.createElement("option", { value: "objects" }, isIt ? "Oggetti" : "Objects")}
                    {React.createElement("option", { value: "allies" }, isIt ? "Alleati" : "Allies")}
                    {React.createElement("option", { value: "abilities" }, isIt ? "Abilità" : "Abilities")}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                    {isIt ? "Seleziona Carta" : "Select Card"}
                  </label>
                  <select
                    data-testid="market-sell-card"
                    value={marketListingCardId}
                    onChange={(e) => setMarketListingCardId(e.target.value)}
                    required
                    className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#e2e8f0] focus:outline-none focus:border-[#cfb53b]/50"
                  >
                    {React.createElement("option", { value: "" }, isIt ? "Seleziona..." : "Select...")}
                    {marketCards.map(c =>
                      React.createElement(
                        "option",
                        { key: c.id, value: c.id },
                        `${c.name} ${c.foil ? "✨" : ""} [${(c.rarity || "common").toUpperCase()}]`
                      )
                    )}
                  </select>
                  {marketCards.length === 0 && (
                    <p className="text-[10px] text-[#64748b] italic mt-1">
                      {isIt ? "Nessuna carta vendibile in questa categoria" : "No sellable cards in this category"}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-[#94a3b8] font-heading uppercase mb-1">
                  {isIt ? "Prezzo in oro" : "Gold price"}
                </label>
                <input
                  data-testid="market-sell-price"
                  type="number"
                  min={0}
                  max={999999}
                  required
                  value={marketListingPrice}
                  onChange={(e) => setMarketListingPrice(e.target.value)}
                  placeholder="0"
                  className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-2 text-sm text-[#cfb53b] focus:outline-none focus:border-[#cfb53b]/50 font-heading"
                />
              </div>
              {isShowcaseFull && (
                <div className="text-xs text-red-400 bg-red-950/20 border border-red-900/30 rounded p-2 text-center">
                  {isIt
                    ? "Spazio in vetrina esaurito! Libera uno slot o sbloccane di nuovi per mettere in vendita altre carte."
                    : "Showcase full! Free a slot or unlock new ones to list more cards."}
                </div>
              )}
              <div className="text-[11px] text-[#64748b] bg-[#cfb53b]/5 border border-[#cfb53b]/10 rounded p-2 flex items-center gap-2">
                <Coins size={12} className="text-[#cfb53b]" />
                <span>
                  {isIt
                    ? "Commissione d'inserimento: 15 Oro (non rimborsabile in caso di ritiro)."
                    : "Listing fee: 15 Gold (non-refundable if cancelled)."}
                </span>
              </div>
              <Button
                type="submit"
                disabled={marketListingSending || !marketListingCardId || isShowcaseFull}
                className="w-full bg-[#cfb53b]/10 text-[#cfb53b] hover:bg-[#cfb53b]/20 border border-[#cfb53b]/30 h-9"
                data-testid="market-sell-submit"
              >
                {marketListingSending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-2" />{isIt ? "Metti in Vetrina" : "List for Sale"}</>}
              </Button>
            </form>

            {/* Listato vetrina */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Carte di altri giocatori */}
              <div className="bg-[#1a1a1d]/60 border border-[#2d3748] rounded-lg p-3 space-y-3">
                <h3 className="font-heading text-sm text-[#cfb53b] flex items-center gap-2 border-b border-[#2d3748] pb-2">
                  <Store size={14} />
                  {isIt ? "Carte in Vetrina" : "Showcase Listings"}
                </h3>
                {marketLoading ? (
                  <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-[#cfb53b]" /></div>
                ) : marketListings.filter(l => l.seller_user_id !== game.user_id).length === 0 ? (
                  <div className="text-center py-8">
                    <Store size={32} className="mx-auto text-[#2d3748] mb-2" />
                    <p className="text-[#94a3b8] text-xs">{isIt ? "Nessuna carta in vendita da altri giocatori" : "No cards listed by other players"}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-1">
                    {marketListings.filter(l => l.seller_user_id !== game.user_id).map(lst => renderMarketCard(lst, false))}
                  </div>
                )}
              </div>

              {/* I miei annunci */}
              <div className="bg-[#1a1a1d]/60 border border-[#2d3748] rounded-lg p-3 space-y-3">
                <h3 className="font-heading text-sm text-[#cfb53b] flex items-center justify-between border-b border-[#2d3748] pb-2">
                  <div className="flex items-center gap-2">
                    <Tag size={14} />
                    <span>{isIt ? `I Miei Annunci (${myListings.length}/${slots})` : `My Listings (${myListings.length}/${slots})`}</span>
                  </div>
                  {slots < 5 && (
                    <Button
                      size="sm"
                      variant="outline"
                      type="button"
                      onClick={handleUnlockSlot}
                      disabled={unlocking || (game?.character?.gold || 0) < nextSlotCost}
                      className="h-6 text-[10px] px-2 py-0 border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 bg-transparent flex items-center gap-1"
                      data-testid="market-unlock-slot-btn"
                    >
                      {unlocking ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Coins size={10} />
                      )}
                      <span>
                        {isIt
                          ? `Sblocca Slot ${slots + 1} (${nextSlotCost} Oro)`
                          : `Unlock Slot ${slots + 1} (${nextSlotCost} Gold)`}
                      </span>
                    </Button>
                  )}
                </h3>
                {marketLoading ? (
                  <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-[#cfb53b]" /></div>
                ) : myListings.length === 0 ? (
                  <div className="text-center py-8">
                    <Tag size={32} className="mx-auto text-[#2d3748] mb-2" />
                    <p className="text-[#94a3b8] text-xs">{isIt ? "Non hai carte in vendita" : "You have no cards listed"}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-1">
                    {myListings.map(lst => renderMarketCard(lst, true))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
